import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Scrolling, SearchPanel, Selection, Editing } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Form, { SimpleItem } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';
import { GENDER_LOOKUP } from '../../../shared/constants';
import { SplitPanel } from '../../utils/split-panel/SplitPanel';
import classNames from 'classnames';

const ClubsLeftPane = React.memo(React.forwardRef(function ClubsLeftPane({ rows, onSelect, onSaving, isSaving, canEdit }, ref) {
    const gridRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        clearSelection: () => gridRef.current?.instance?.clearSelection(),
    }), []);
    return (
        <div className='dx-card' style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <DataGrid
                ref={gridRef}
                className='grid theme-dependent'
                dataSource={rows}
                keyExpr='clubId'
                columnAutoWidth
                rowAlternationEnabled
                hoverStateEnabled
                showBorders={false}
                repaintChangesOnly
                onSelectionChanged={(e) => { const row = e.selectedRowsData?.[0]; onSelect(row ? row.clubId : null); }}
                onSaving={onSaving}
                loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
                style={{ flex: 1, minHeight: 0 }}
            >
                <SearchPanel visible placeholder='Suche nach Vereinsname...' />
                <Selection mode='single' />
                <Column dataField='name' caption='Vereinsname' />
                <Column dataField='city' caption='Ort' width={120} />
                <Column dataField='nation' caption='Nation' width={70} />
                <Editing mode='popup' allowAdding={canEdit} useIcons />
                <Scrolling mode='virtual' />
            </DataGrid>
        </div>
    );
}));

export function CompetitionClubs({ competitionId, data, athletes, accessToken, onEntityChange }) {
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const dataRef = useRef(data);
    useEffect(() => { dataRef.current = data; }, [data]);

    const canEdit = !!accessToken;

    const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    useEffect(() => {
        if (!selectedId) { setFormData(null); setIsDirty(false); return; }
        const item = dataRef.current?.find(c => c.clubId === selectedId);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleSelect = useCallback((id) => setSelectedId(id), []);
    const leftPaneRef = useRef(null);

    const updateField = useCallback((field, value) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : prev);
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData || !accessToken || !selectedId) return;
        setIsSaving(true);
        try {
            const current = dataRef.current?.find(c => c.clubId === selectedId) || {};
            const saved = await competitionEntityApi.clubs.update(competitionId, selectedId, { ...current, ...formData, clubId: selectedId, competitionId }, accessToken);
            onEntityChange?.('Club', 'update', saved);
            setIsDirty(false);
            setIsEditing(false);
            notify('Gespeichert', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, formData, onEntityChange, selectedId]);

    const handleCancel = useCallback(() => {
        const item = dataRef.current?.find(c => c.clubId === selectedId);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleDelete = useCallback(async () => {
        if (!accessToken || !selectedId) return;
        if (!window.confirm('Verein wirklich löschen?')) return;
        setIsSaving(true);
        try {
            await competitionEntityApi.clubs.remove(competitionId, selectedId, accessToken);
            onEntityChange?.('Club', 'delete', { clubId: selectedId });
            setSelectedId(null);
            leftPaneRef.current?.clearSelection();
            notify('Gelöscht', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Löschen', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange, selectedId]);

    const handleGridSaving = useCallback(async (e) => {
        if (!e.changes?.length) return;
        if (!accessToken) { notify('Kein Zugriffstoken verfügbar.', 'warning', 3000); e.cancel = true; return; }
        e.cancel = true;
        setIsSaving(true);
        try {
            for (const change of e.changes) {
                if (change.type === 'insert') {
                    const payload = { ...change.data, competitionId };
                    const saved = await competitionEntityApi.clubs.insert(competitionId, payload, accessToken);
                    onEntityChange?.('Club', 'insert', saved);
                }
            }
            e.component.cancelEditData();
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange]);

    const clubAthletes = useMemo(() => {
        if (!selectedId || !athletes) return [];
        return athletes.filter(a => a.clubId === selectedId && a.isRelay !== true);
    }, [selectedId, athletes]);

    const renderFormToolbar = useCallback((title) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            {canEdit && (
                <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                        <>
                            <Button text='Speichern' type='default' stylingMode='contained' disabled={isSaving} onClick={handleSave} />
                            <Button text='Abbrechen' stylingMode='outlined' disabled={isSaving} onClick={handleCancel} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={isSaving} onClick={handleDelete} />
                        </>
                    ) : (
                        <>
                            <Button text='Bearbeiten' icon='edit' stylingMode='outlined' disabled={!formData} onClick={() => setIsEditing(true)} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={!formData || isSaving} onClick={handleDelete} />
                        </>
                    )}
                </div>
            )}
        </div>
    ), [canEdit, formData, handleCancel, handleDelete, handleSave, isEditing, isSaving]);

    const renderDetail = () => {
        if (!formData) {
            return (
                <div className='dx-card' style={{ padding: 20, color: '#888' }}>
                    Bitte einen Verein aus der Liste links auswählen.
                </div>
            );
        }
        const f = formData;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16 }}>
                    {renderFormToolbar(f.name || f.shortname || 'Verein')}
                    <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateField(e.dataField, e.value)}>
                        <SimpleItem dataField='name' label={{ text: 'Vereinsname' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='shortname' label={{ text: 'Kurzname' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='nation' label={{ text: 'Nation' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='license' label={{ text: 'Lizenz' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='lsvNumber' label={{ text: 'LSV-Nummer' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='contactName' label={{ text: 'Ansprechpartner' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='street' label={{ text: 'Straße' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='plz' label={{ text: 'PLZ' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='city' label={{ text: 'Ort' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='phone' label={{ text: 'Telefon' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='mobil' label={{ text: 'Mobil' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='mail1' label={{ text: 'E-Mail 1' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='mail2' label={{ text: 'E-Mail 2' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='discount' label={{ text: 'Rabatt' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='flatrate' label={{ text: 'Flatrate' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='comment' label={{ text: 'Kommentar' }} editorType='dxTextArea' editorOptions={{ stylingMode: 'filled', height: 60 }} />
                    </Form>
                </div>
                <div className='dx-card' style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Athleten dieses Vereins</div>
                    <DataGrid
                        dataSource={clubAthletes}
                        keyExpr='athleteId'
                        columnAutoWidth
                        showBorders={false}
                        hoverStateEnabled
                        rowAlternationEnabled
                        noDataText='Keine Athleten für diesen Verein'
                    >
                        <Column dataField='name' caption='Nachname' width={130} />
                        <Column dataField='firstname' caption='Vorname' width={130} />
                        <Column dataField='gender' caption='Geschl.' width={70} lookup={{ dataSource: GENDER_LOOKUP, valueExpr: 'id', displayExpr: 'name' }} />
                        <Column dataField='birthdate' caption='Geburtsdatum' dataType='date' format='dd.MM.yyyy' width={120} />
                        <Column dataField='license' caption='Lizenz' width={100} />
                        <Scrolling mode='virtual' />
                    </DataGrid>
                </div>
            </div>
        );
    };

    return (
        <div className='competition-section'>
            <div className='section-header'>
                <div>
                    <div className='section-title'>Vereine</div>
                    <div className='section-subtitle'>Teilnehmende Vereine und Kontaktdaten</div>
                </div>
                <div className='section-pill subtle'>{rows.length} Verein{rows.length !== 1 ? 'e' : ''}</div>
            </div>

            <SplitPanel
                storageKey='splitPanel.clubs'
                defaultLeft={320}
                minLeft={160}
                minRight={200}
                left={<ClubsLeftPane ref={leftPaneRef} rows={rows} onSelect={handleSelect} onSaving={handleGridSaving} isSaving={isSaving} canEdit={canEdit} />}
                right={renderDetail()}
            />
        </div>
    );
}
